import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

/* ── Section wrappers ── */
export const StyledSection = styled.section<{ $bg?: string }>`
  padding: 60px 20px;
  background: ${({ $bg }) => $bg || CSSVars.white};
  border-top: 1px solid rgba(0, 0, 0, 0.07);

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 80px 30px;
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 110px 40px;
  }
`;

export const StyledInner = styled.div<{ $maxWidth?: string }>`
  max-width: ${({ $maxWidth }) => $maxWidth || '960px'};
  margin: 0 auto;
`;

export const StyledSectionLabel = styled.p`
  font-size: ${CSSVars.fontSizeXS};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${CSSVars.blue500};
  margin-bottom: 14px;
`;

export const StyledSectionHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${CSSVars.black};
  margin-bottom: 18px;
  line-height: 1.25;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    font-size: ${CSSVars.fontSize2XL};
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    font-size: ${CSSVars.fontSize3XL};
  }
`;

export const StyledSectionIntro = styled.p`
  font-size: ${CSSVars.fontSizeMD};
  color: ${CSSVars.gray600};
  line-height: 1.75;
  max-width: 680px;
  font-weight: 300;
`;

/* ── Step cards (How It Works) ── */
export const StyledStepsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 36px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledStepCard = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 22px 18px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 28px 24px; }
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

  .step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(76, 79, 255, 0.08);
    color: ${CSSVars.blue500};
    font-weight: 700;
    font-size: ${CSSVars.fontSizeSM};
    margin-bottom: 16px;
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 8px;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    font-weight: 300;

    a {
      color: ${CSSVars.blue500};
      font-weight: 600;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

/* ── Scenario cards ── */
export const StyledScenariosGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 36px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StyledScenarioCard = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 22px 18px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 28px 24px; }
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    font-weight: 300;
    margin-bottom: 12px;
  }

  .scenario-widgets {
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 600;
    color: ${CSSVars.blue500};
    margin-bottom: 8px;
  }

  .scenario-outcome {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    font-style: italic;
  }
`;

/* ── Widget guide table ── */
export const StyledDataTable = styled.div`
  margin-top: 36px;
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 480px;
  }

  thead tr {
    background: linear-gradient(90deg, #09093a 0%, #0d0e30 100%);
  }

  thead th {
    color: ${CSSVars.gray300};
    padding: 12px 12px;
    @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 16px 20px; }
    font-size: ${CSSVars.fontSizeXS};
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-align: left;
  }

  tbody tr {
    background: ${CSSVars.white};
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);

    &:hover {
      background: rgba(76, 79, 255, 0.04);
    }
  }

  tbody td {
    padding: 10px 12px;
    color: ${CSSVars.gray700};
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 300;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      padding: 14px 20px;
      font-size: ${CSSVars.fontSizeSM};
    }
  }

  .col-name {
    font-weight: 600;
    color: ${CSSVars.black};
  }

  .col-link a {
    color: ${CSSVars.blue500};
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

/* ── Stat cards ── */
export const StyledStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-top: 36px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    gap: 20px;
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const StyledStatCard = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 20px 14px;
  text-align: center;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 28px 20px; }
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

  .stat-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: ${CSSVars.blue500};
    margin-bottom: 6px;
    line-height: 1.2;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .stat-label {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 8px;
  }

  p {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    line-height: 1.6;
    font-weight: 300;
  }
`;

/* ── Integration steps ── */
export const StyledIntegrationSteps = styled.ol`
  margin-top: 32px;
  list-style: none;
  padding: 0;
  counter-reset: step;

  li {
    counter-increment: step;
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 18px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);

    &:last-child { border-bottom: none; }

    &::before {
      content: counter(step);
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: rgba(76, 79, 255, 0.08);
      color: ${CSSVars.blue500};
      font-weight: 700;
      font-size: ${CSSVars.fontSizeSM};
    }

    span {
      font-size: ${CSSVars.fontSizeMD};
      color: ${CSSVars.gray700};
      line-height: 1.65;
      font-weight: 300;
      padding-top: 4px;
    }
  }
`;

/* ── Related articles ── */
export const StyledArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 36px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledArticleCard = styled.a`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 24px 20px;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  h3 {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    color: ${CSSVars.black};
    line-height: 1.4;
  }

  p {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    line-height: 1.6;
    font-weight: 300;
    flex: 1;
  }

  .read-more {
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    color: ${CSSVars.blue500};
  }
`;
